import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Paper,
  InputAdornment,
  Tooltip,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import BookIcon from '@mui/icons-material/Book';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PublicIcon from '@mui/icons-material/Public';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import ProModal from '../../common/ProModal';

const LoreEditor = ({ lore = [], onAddLore, onEditLore, onDeleteLore }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const accentColor = isDarkMode ? '#ffcc80' : '#e65100';
  const accentBg = isDarkMode ? 'rgba(255, 171, 64, 0.18)' : 'rgba(255, 171, 64, 0.08)';
  const accentBorder = isDarkMode ? 'rgba(255, 183, 77, 0.45)' : 'rgba(255, 152, 0, 0.24)';
  const chipBg = isDarkMode ? 'rgba(255, 167, 38, 0.25)' : 'rgba(255, 167, 38, 0.15)';
  const chipHover = isDarkMode ? 'rgba(255, 167, 38, 0.35)' : 'rgba(255, 167, 38, 0.25)';
  const cardBg = isDarkMode ? 'rgba(33, 17, 6, 0.95)' : '#fff8f0';
  const textPrimary = isDarkMode ? '#fff3e0' : '#4e342e';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.75)' : '#6d4c41';

  // State for dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLore, setSelectedLore] = useState(null);
  const [newLore, setNewLore] = useState({
    name: '',
    description: '',
    details: '',
    tags: [],
    importance: 'medium',
    category: 'lore',
    significance: '',
    origins: '',
    variations: '',
    loreType: ''
  });

  // State for template management
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('book');

  // Load custom templates from localStorage on component mount
  useEffect(() => {
    const savedTemplates = localStorage.getItem('loreCustomTemplates');
    if (savedTemplates) {
      try {
        setCustomTemplates(JSON.parse(savedTemplates));
      } catch (error) {
        console.error('Error parsing custom templates:', error);
      }
    }
  }, []);

  // Save custom templates to localStorage when they change
  useEffect(() => {
    localStorage.setItem('loreCustomTemplates', JSON.stringify(customTemplates));
  }, [customTemplates]);

  // Importance levels for dropdown
  const importanceLevels = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  // Predefined lore templates - optimized for film/screenwriting with cinematic details
  const loreTemplates = [
    {
      name: 'creation_myth',
      label: 'Creation Myth',
      icon: <PublicIcon sx={{ color: '#e65100' }} />,
      description: 'How the world began—the story that shapes everything',
      data: {
        description: 'The story of how everything began. Every culture tells it differently, and those differences matter. This is the foundation myth that shapes how people see their place in the world.',
        importance: 'high',
        significance: 'Defines the world\'s fundamental nature. Explains why things are the way they are. Justifies current power structures or challenges them.',
        origins: 'Lost in time—claimed to be revealed truth, passed down from the first people, or discovered in ancient texts.',
        variations: 'Different cultures emphasize different aspects. Some versions are suppressed. The "true" version may be very different from what\'s commonly told.',
        details: '**Cinematic Potential:** The creation story told in voiceover or flashback. Characters debating which version is true. A discovery that challenges the accepted myth.\n\n**Story Hooks:** What if the creation myth is literally true? What if it\'s a lie covering something worse? What if the creation isn\'t finished?',
        mood: 'Primordial and vast. The weight of origins.',
        visualStyle: 'Abstract imagery, cosmic scale, the transition from chaos to order.'
      }
    },
    {
      name: 'prophecy',
      label: 'Prophecy',
      icon: <AutoStoriesIcon sx={{ color: '#e65100' }} />,
      description: 'Words that shape the future—or seem to',
      data: {
        description: 'A prediction of events to come, spoken by someone who claimed to see beyond time. Whether it\'s genuine foresight or self-fulfilling expectation, people act on it.',
        importance: 'high',
        significance: 'Drives character motivation. Creates dramatic irony. The audience knows what\'s coming—or thinks they do.',
        origins: 'A prophet, oracle, dying sage, or discovered text. The source\'s credibility matters—or doesn\'t, if people believe anyway.',
        variations: 'Multiple interpretations. Deliberate mistranslations. The version the powerful want people to hear versus the original.',
        details: '**Cinematic Potential:** The prophecy revealed at a crucial moment. Characters trying to fulfill or prevent it. The twist when it comes true in an unexpected way.\n\n**Story Hooks:** The chosen one doesn\'t want to be chosen. The prophecy was faked. It\'s already been fulfilled and no one noticed.',
        mood: 'Ominous inevitability. Hope or dread depending on interpretation.',
        visualStyle: 'Cryptic imagery, symbolic visuals, the moment of realization.'
      }
    },
    {
      name: 'legend',
      label: 'Hero Legend',
      icon: <EmojiEventsIcon sx={{ color: '#e65100' }} />,
      description: 'The hero who defined an age—truth buried under glory',
      data: {
        description: 'The story of a legendary figure whose deeds shaped history. The real person—if they existed—is long buried under layers of embellishment, propaganda, and wishful thinking.',
        importance: 'high',
        significance: 'Provides a model for heroism. Used to justify current actions. The gap between legend and truth is where drama lives.',
        origins: 'Based on a real person, probably. Or a composite of several. Or entirely invented for political purposes.',
        variations: 'The version the winners tell. The version the losers remember. The version that\'s actually true.',
        details: '**Cinematic Potential:** Flashbacks to the "real" events. A descendant living in the legend\'s shadow. Discovering the hero was actually the villain.\n\n**Story Hooks:** The legendary hero is still alive. Their greatest deed was actually their greatest crime. Someone is recreating their journey.',
        mood: 'Inspiration and burden. The weight of living up to legends.',
        visualStyle: 'Heroic imagery contrasted with gritty reality. Statues and paintings versus the truth.'
      }
    },
    {
      name: 'curse',
      label: 'Ancient Curse',
      icon: <MenuBookIcon sx={{ color: '#e65100' }} />,
      description: 'Words of power that echo through generations',
      data: {
        description: 'A malediction laid down long ago, still working its poison through bloodlines, places, or objects. Whether supernatural or psychological, its effects are real.',
        importance: 'high',
        significance: 'Creates inherited conflict. Explains recurring tragedy. The curse must be understood before it can be broken—if it can be broken.',
        origins: 'A wrong that demanded vengeance. A dying breath. A bargain gone wrong. The original sin that started everything.',
        variations: 'What the cursed believe versus what actually happened. The curse-layer\'s version. What would actually break it.',
        details: '**Cinematic Potential:** The curse manifesting at the worst moment. Tracing the curse back to its origin. The moral complexity of the original wrong.\n\n**Story Hooks:** The curse is actually protection. Breaking it requires becoming what you hate. The curse-layer was the victim.',
        mood: 'Inescapable doom. Generational guilt. The sins of the fathers.',
        visualStyle: 'Recurring visual motifs. The moment of the curse. Its effects through time.'
      }
    },
    {
      name: 'forbidden_knowledge',
      label: 'Forbidden Knowledge',
      icon: <MenuBookIcon sx={{ color: '#e65100' }} />,
      description: 'Truth too dangerous to know—but someone always looks',
      data: {
        description: 'Information that\'s been hidden, suppressed, or lost because knowing it is dangerous. The danger might be to the knower, to society, or to those who benefit from ignorance.',
        importance: 'high',
        significance: 'The ultimate MacGuffin. Drives plots about discovery and its consequences. The question of whether some things shouldn\'t be known.',
        origins: 'Discovered and hidden. Revealed and suppressed. Known by a few who guard it—or exploit it.',
        variations: 'Fragments scattered across sources. Encoded or encrypted. The version that\'s a trap for seekers.',
        details: '**Cinematic Potential:** The moment of discovery. The choice of what to do with dangerous truth. The cost of knowing.\n\n**Story Hooks:** The knowledge is about to be rediscovered. Someone is killing everyone who knows. The protagonist already knows but doesn\'t realize it.',
        mood: 'Terrible revelation. The burden of truth. Pandora\'s box.',
        visualStyle: 'Hidden texts and symbols. The seeker\'s obsession. The moment everything changes.'
      }
    },
    {
      name: 'lost_civilization',
      label: 'Lost Civilization',
      icon: <PublicIcon sx={{ color: '#e65100' }} />,
      description: 'They were greater than us—and something destroyed them',
      data: {
        description: 'A civilization that rose higher than the present one and fell. Their ruins dot the landscape. Their artifacts still work. What destroyed them could destroy us too.',
        importance: 'high',
        significance: 'Provides mystery and wonder. Source of powerful artifacts. Warning about hubris. The question of what really happened.',
        origins: 'Rose in the distant past. Achieved wonders. Fell suddenly or gradually. The truth of their fall is the real story.',
        variations: 'What survivors remember. What scholars theorize. What the ruins actually show. What the current powers want people to believe.',
        details: '**Cinematic Potential:** Exploring the ruins. Activating ancient technology. Discovering they\'re not as gone as believed.\n\n**Story Hooks:** They didn\'t fall—they left. They\'re coming back. We\'re repeating their mistakes. Their "fall" was actually an ascension.',
        mood: 'Wonder and warning. Echoes of greatness. The hubris of civilization.',
        visualStyle: 'Overgrown ruins. Technology beyond understanding. The contrast between past glory and present decay.'
      }
    },
    {
      name: 'dark_secret',
      label: 'Dark Secret',
      icon: <HistoryEduIcon sx={{ color: '#e65100' }} />,
      description: 'The truth they buried—but graves don\'t always hold',
      data: {
        description: 'Something terrible happened and was covered up. The people responsible are dead, but their descendants benefit from the lie. The truth is still out there, waiting.',
        importance: 'high',
        significance: 'Reveals the rot beneath respectable surfaces. Creates moral complexity. The question of whether old wrongs should be exposed.',
        origins: 'A crime, a betrayal, a catastrophe that was someone\'s fault. Covered up by those with power to protect.',
        variations: 'The official story. What the guilty knew. What the victims remembered. The evidence that survived.',
        details: '**Cinematic Potential:** The investigation that uncovers truth. The confrontation with descendants of the guilty. The choice of what to do with the truth.\n\n**Story Hooks:** The cover-up is ongoing. Exposing it will destroy innocents. The "victims" weren\'t innocent either.',
        mood: 'Buried guilt. The weight of secrets. Justice delayed.',
        visualStyle: 'Documents and evidence. The contrast between public face and private truth. The moment of revelation.'
      }
    },
    {
      name: 'sacred_text',
      label: 'Sacred Text',
      icon: <LocalLibraryIcon sx={{ color: '#e65100' }} />,
      description: 'Words that command belief—and justify anything',
      data: {
        description: 'A text considered divinely inspired or absolutely authoritative. People live and die by its words—or by particular interpretations of them.',
        importance: 'high',
        significance: 'Source of law, morality, and conflict. Different interpretations drive different factions. The text itself may be different from what\'s taught.',
        origins: 'Claimed divine revelation. Ancient wisdom. The words of a founder. Compiled and edited over centuries.',
        variations: 'Orthodox interpretation. Heretical readings. The original versus translations. What was edited out.',
        details: '**Cinematic Potential:** Scholars debating meaning. A passage that changes everything. The discovery of an original manuscript.\n\n**Story Hooks:** The text has been deliberately altered. A lost section is found. The text predicts current events—too accurately.',
        mood: 'Absolute authority. The weight of scripture. Faith and doubt.',
        visualStyle: 'Ancient manuscripts. Scholars and believers. The text as physical object.'
      }
    },
    {
      name: 'folk_tale',
      label: 'Folk Tale',
      icon: <HistoryEduIcon sx={{ color: '#e65100' }} />,
      description: 'Stories the common people tell—with more truth than they know',
      data: {
        description: 'A story passed down among ordinary people, dismissed by the educated as superstition. But folk tales often preserve real warnings in symbolic form.',
        importance: 'medium',
        significance: 'Contains hidden truth. Reveals what common people fear and value. Often more accurate than official history.',
        origins: 'Oral tradition. Grandmother\'s warnings. Stories told to children that adults have forgotten to take seriously.',
        variations: 'Regional versions. The scary version told at night. The sanitized version for children. The original that adults don\'t repeat.',
        details: '**Cinematic Potential:** The folk tale told, then shown to be true. A character who takes the old stories seriously. The moment when everyone realizes grandmother was right.\n\n**Story Hooks:** The monster in the story is real. The folk tale is instructions in disguise. The story is a warning about something that\'s happening again.',
        mood: 'Homespun wisdom. Dismissed truth. The power of stories.',
        visualStyle: 'Storytelling scenes. The tale visualized. Reality matching the story.'
      }
    },
    {
      name: 'historical_event',
      label: 'Historical Event',
      icon: <HistoryEduIcon sx={{ color: '#e65100' }} />,
      description: 'What actually happened—and what people think happened',
      data: {
        description: 'A real event from the past that shaped the present. The official version serves current interests. The truth is more complicated—and more useful.',
        importance: 'medium',
        significance: 'Explains current conflicts. Provides precedent. The gap between history and memory is where manipulation happens.',
        origins: 'Documented in records, remembered in stories, commemorated in monuments—each version serving different purposes.',
        variations: 'Winner\'s history. Loser\'s memory. What the records actually say. What was deliberately destroyed.',
        details: '**Cinematic Potential:** Flashbacks to the event. Characters discovering the truth. The past repeating in the present.\n\n**Story Hooks:** The hero of the event was actually the villain. A survivor is still alive. Evidence of what really happened surfaces.',
        mood: 'The weight of history. Truth versus narrative. The past\'s grip on the present.',
        visualStyle: 'Historical recreation. Documents and monuments. Then and now.'
      }
    }
  ];

  // Dialog handlers
  const handleOpenDialog = (lore = null) => {
    if (lore) {
      setSelectedLore(lore);
      setNewLore({ 
        ...lore,
        tags: lore.tags || [] 
      });
    } else {
      setSelectedLore(null);
      setNewLore({
        name: '',
        description: '',
        details: '',
        tags: [],
        importance: 'medium',
        category: 'lore',
        significance: '',
        origins: '',
        variations: '',
        loreType: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLore(null);
    setNewLore({
      name: '',
      description: '',
      details: '',
      tags: [],
      importance: 'medium',
      category: 'lore',
      significance: '',
      origins: '',
      variations: '',
      loreType: ''
    });
  };

  const handleSave = () => {
    if (!newLore.name) return;

    const loreToSave = {
      ...newLore,
      id: selectedLore?.id || newLore.id || Date.now().toString()
    };

    if (selectedLore) {
      onEditLore(loreToSave);
    } else {
      onAddLore(loreToSave);
    }
    handleCloseDialog();
  };

  // Template functions
  const applyLoreTemplate = (templateName) => {
    const template = loreTemplates.find(t => t.name === templateName);
    if (template) {
      // Build cinematic details from new fields
      const cinematicDetails = template.data.details + 
        (template.data.mood ? `\n\n**Mood:** ${template.data.mood}` : '') +
        (template.data.visualStyle ? `\n\n**Visual Style:** ${template.data.visualStyle}` : '');
      
      setNewLore({
        ...newLore,
        description: template.data.description,
        importance: template.data.importance,
        significance: template.data.significance,
        origins: template.data.origins,
        variations: template.data.variations,
        details: cinematicDetails,
        loreType: template.name,
        tags: newLore.tags || []
      });
    }
  };

  const applyCustomTemplate = (templateName) => {
    const template = customTemplates.find(t => t.name === templateName);
    if (template) {
      setNewLore({
        ...newLore,
        description: template.data.description,
        importance: template.data.importance,
        significance: template.data.significance,
        origins: template.data.origins,
        variations: template.data.variations,
        details: template.data.details || '',
        loreType: template.name,
        tags: newLore.tags || []
      });
    }
  };

  const saveAsTemplate = () => {
    if (!newTemplateName) return;

    const templateIcon = loreTemplates.find(t => t.name === newTemplateIcon)?.icon || <BookIcon sx={{ color: '#e65100' }} />;
    
    const newTemplate = {
      name: newTemplateName.toLowerCase().replace(/\s+/g, '_'),
      label: newTemplateName,
      icon: templateIcon,
      description: newTemplateDescription || 'Custom template',
      data: {
        description: newLore.description,
        importance: newLore.importance,
        significance: newLore.significance,
        origins: newLore.origins,
        variations: newLore.variations,
        details: newLore.details
      }
    };

    setCustomTemplates([...customTemplates, newTemplate]);
    setShowSaveTemplateDialog(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateIcon('book');
  };

  const deleteCustomTemplate = (templateName) => {
    setCustomTemplates(customTemplates.filter(t => t.name !== templateName));
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Tooltip title="Add new lore" arrow>
          <span>
            <IconButton
              onClick={() => handleOpenDialog()}
              aria-label="Add new lore"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                color: accentColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: accentBg
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="h5" component="h2" sx={{ 
          fontWeight: 600,
          color: accentColor
        }}>
          Lore & Legends
        </Typography>
      </Box>

      {lore && lore.length === 0 ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 2,
            border: `1px dashed ${accentBorder}`,
            bgcolor: isDarkMode ? 'rgba(255, 183, 77, 0.18)' : 'rgba(255, 183, 77, 0.12)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: accentColor }}>
            No lore yet
          </Typography>
          <Typography variant="body2" color={textSecondary}>
            Tap the “+” icon to add your first lore entry and enrich your world.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {lore.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.id || `${item.name}-${index}`}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: cardBg,
                border: `1px solid ${accentBorder}`,
                boxShadow: isDarkMode ? '0 14px 30px rgba(0,0,0,0.45)' : '0 12px 26px rgba(230,81,0,0.18)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: isDarkMode ? '0 18px 34px rgba(0,0,0,0.55)' : '0 18px 32px rgba(230,81,0,0.28)'
                }
              }}>
                <Box sx={{ 
                  p: 1, 
                  background: isDarkMode
                    ? 'linear-gradient(120deg, rgba(255,138,0,0.35), rgba(230,81,0,0.45))'
                    : 'linear-gradient(120deg, rgba(255,183,77,0.3), rgba(255,138,0,0.2))',
                  borderBottom: `1px solid ${accentBorder}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: textPrimary
                    }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.3,
                          borderRadius: '999px',
                          backgroundColor: chipBg,
                          border: `1px solid ${accentBorder}`,
                          color: accentColor,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: 'uppercase'
                        }}
                      >
                        Lore
                      </Box>
                      {item.importance && (
                        <Chip 
                          size="small" 
                          label={item.importance.charAt(0).toUpperCase() + item.importance.slice(1)} 
                          sx={{ 
                            fontWeight: 600,
                            height: '22px',
                            bgcolor: chipBg,
                            color: textPrimary,
                            border: `1px solid ${accentBorder}`
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 2, flexGrow: 1 }}>
                  {item.description && (
                    <Typography variant="body2" sx={{ mb: 1, color: textSecondary }}>
                      {item.description}
                    </Typography>
                  )}
                  
                  {item.significance && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: textSecondary }}>
                        Significance:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: textSecondary
                      }}>
                        {item.significance}
                      </Typography>
                    </Box>
                  )}
                  
                  {item.origins && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: textSecondary }}>
                        Origins:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: textSecondary
                      }}>
                        {item.origins}
                      </Typography>
                    </Box>
                  )}
                  
                  {item.tags && item.tags.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            bgcolor: chipBg,
                            color: accentColor,
                            border: `1px solid ${accentBorder}`
                          }} 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                
                {/* Template Info */}
                {item.loreType && (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      mx: 2,
                      mb: 2,
                      borderRadius: 2, 
                      bgcolor: accentBg,
                      border: `1px dashed ${accentColor}`
                    }}
                  >
                    <Typography variant="caption" sx={{ color: accentColor }}>
                      {loreTemplates.find(t => t.name === item.loreType)?.label || 'Lore'} Template
                    </Typography>
                  </Paper>
                )}
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  p: 1, 
                  pt: 0,
                  borderTop: `1px solid ${accentBorder}`
                }}>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />} 
                    onClick={() => handleOpenDialog(item)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<DeleteIcon />} 
                    color="error"
                    onClick={() => onDeleteLore(item.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog for editing lore */}
      <ProModal
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedLore ? 'Edit Lore Item' : 'Add New Lore Item'}
        eyebrow="LORE & LEGENDS"
        icon={<AutoStoriesIcon />}
        maxWidth="lg"
        actions={(
          <>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #ff9800, #e65100)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #fb8c00, #c62828)'
                }
              }}
            >
              {selectedLore ? 'Update Lore' : 'Add Lore'}
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
              {/* Basic Information Section */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem' }} /> Basic Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Lore Name"
                      fullWidth
                      value={newLore.name}
                      onChange={(e) => setNewLore({ ...newLore, name: e.target.value })}
                      required
                      placeholder="Give your lore item a memorable name"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BookIcon sx={{ color: '#e65100' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel id="importance-label">Narrative Importance</InputLabel>
                      <Select
                        labelId="importance-label"
                        label="Narrative Importance"
                        value={newLore.importance}
                        onChange={(e) => setNewLore({ ...newLore, importance: e.target.value })}
                      >
                        {importanceLevels.map((level) => (
                          <MenuItem key={level.value} value={level.value}>
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        How significant is this lore to your story?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Brief Description"
                      fullWidth
                      multiline
                      rows={2}
                      value={newLore.description}
                      onChange={(e) => setNewLore({ ...newLore, description: e.target.value })}
                      placeholder="A logline for this lore—what is it and why does it matter to your story?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Lore Classification */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 152, 0, 0.1)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#e65100', display: 'flex', alignItems: 'center' }}>
                  <MenuBookIcon sx={{ mr: 1, fontSize: '1rem' }} /> Lore Classification
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel id="lore-type-label">Lore Type</InputLabel>
                      <Select
                        labelId="lore-type-label"
                        label="Lore Type"
                        value={newLore.loreType || ''}
                        onChange={(e) => setNewLore({ ...newLore, loreType: e.target.value })}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {loreTemplates.map((template) => (
                          <MenuItem key={template.name} value={template.name}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ mr: 1 }}>{template.icon}</Box>
                              {template.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        What category of lore is this?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={newLore.tags || []}
                      onChange={(e, newValue) => setNewLore({ ...newLore, tags: newValue })}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(255, 152, 0, 0.1)',
                              color: '#e65100'
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tags"
                          placeholder="Add tags"
                          helperText="Press Enter to add tags"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Detailed Information */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 152, 0, 0.1)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#e65100', display: 'flex', alignItems: 'center' }}>
                  <HistoryEduIcon sx={{ mr: 1, fontSize: '1rem' }} /> Detailed Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Significance"
                      value={newLore.significance || ''}
                      onChange={(e) => setNewLore({ ...newLore, significance: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="How does this lore drive plot or character? What scenes could it create?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Origins"
                      value={newLore.origins || ''}
                      onChange={(e) => setNewLore({ ...newLore, origins: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Where did this come from? Who told it first? Is the origin itself part of the mystery?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Variations"
                      value={newLore.variations || ''}
                      onChange={(e) => setNewLore({ ...newLore, variations: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Conflicting versions create conflict. What do different groups believe? Which version is true?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Additional Details"
                      value={newLore.details || ''}
                      onChange={(e) => setNewLore({ ...newLore, details: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Cinematic potential, story hooks, mood, visual style—how would this appear on screen?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Accordion 
                defaultExpanded={false}
                sx={{ 
                  bgcolor: accentBg, 
                  borderRadius: '1 !important', 
                  border: `1px solid ${accentBorder}`,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  mb: 3
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                  sx={{ 
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1 }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                    <AutoStoriesIcon sx={{ mr: 1, fontSize: '1rem' }} /> Quick Templates
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: textSecondary }}>
                    Apply a template to quickly fill in common lore types.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: accentColor }}>
                      Standard Templates
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {loreTemplates.map((template) => (
                        <Chip
                          key={template.name}
                          icon={template.icon}
                          label={template.label}
                          onClick={() => applyLoreTemplate(template.name)}
                          sx={{
                            bgcolor: chipBg,
                            color: accentColor,
                            border: `1px solid ${accentBorder}`,
                            '&:hover': {
                              bgcolor: chipHover,
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {customTemplates && customTemplates.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: accentColor }}>
                        Custom Templates
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {customTemplates.map((template) => (
                          <Chip
                            key={template.name}
                            icon={<BookmarkIcon />}
                            label={template.name}
                            onClick={() => applyCustomTemplate(template.name)}
                            sx={{
                              bgcolor: chipBg,
                              color: accentColor,
                              border: `1px solid ${accentBorder}`,
                              '&:hover': {
                                bgcolor: chipHover,
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              
              <Paper elevation={0} sx={{ p: 2, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <BookmarkIcon sx={{ mr: 1, fontSize: '1rem' }} /> Save as Template
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2, color: textSecondary }}>
                  Save your current settings as a custom template for future use.
                </Typography>
                
                <TextField
                  label="Template Name"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  fullWidth
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  placeholder="Enter a name for your template"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BookmarkIcon sx={{ color: accentColor }} />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={saveAsTemplate}
                  disabled={!newTemplateName.trim()}
                  startIcon={<SaveIcon />}
                  sx={{ 
                    borderRadius: '8px',
                    borderColor: accentBorder,
                    color: accentColor,
                    '&:hover': {
                      borderColor: accentColor,
                      bgcolor: chipBg,
                    }
                  }}
                >
                  Save Template
                </Button>
              </Paper>
          </Grid>
        </Grid>
      </ProModal>
      
      {/* Dialog for saving template */}
      <ProModal
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title="Save as Template"
        eyebrow="LORE & LEGENDS"
        icon={<BookmarkIcon />}
        maxWidth="sm"
        actions={(
          <>
            <Button 
              onClick={() => setShowSaveTemplateDialog(false)}
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={saveAsTemplate}
              disabled={!newTemplateName.trim()}
              startIcon={<SaveIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #ff9800, #e65100)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #fb8c00, #c62828)'
                }
              }}
            >
              Save Template
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Template Name"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Template Description"
            value={newTemplateDescription}
            onChange={(e) => setNewTemplateDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel id="template-icon-label">Icon</InputLabel>
            <Select
              labelId="template-icon-label"
              label="Icon"
              value={newTemplateIcon}
              onChange={(e) => setNewTemplateIcon(e.target.value)}
            >
              {loreTemplates.map((template) => (
                <MenuItem key={template.name} value={template.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1 }}>{template.icon}</Box>
                    {template.label} Icon
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </ProModal>
    </Box>
  );
};

export default LoreEditor;
